import type { AuthAccountWithPassType } from 'bc-artifacts-client/api';
import type { TimeSeriesMetricLabels } from 'bc-model';
import type {
  DurationMetricName,
  LogEventFunc,
  PromCounterMetricName,
  PromDurationMetricName,
  PromGaugeMetricName,
} from './types';
import getViewName from './getViewName';

interface DurationMetricContext {
  componentName?: string;
  commandName?: string;
  dropType?: string;
  dropSource?: string;
  mapFeature?: string;
  collabType?: string;
  collabMethod?: string;
  collabPath?: string;
  printCompletionMethod?: string;
}

interface DurationMetricOptions {
  description?: string;
  context?: DurationMetricContext;
}

type OnComplete = (modifiedOptions?: DurationMetricOptions) => void;

type OnDurationComplete = (start: number, duration: number) => void;

interface MetricsAdapter {
  logDuration: (name: DurationMetricName, options?: DurationMetricOptions) => OnDurationComplete;
  setUser: (user: AuthAccountWithPassType | null) => void;
  setViewName: (name: string) => void;
  logEvent: LogEventFunc;
  logWarning: (message: string, options?: Record<string, unknown>) => void;
  logError: (error: Error, options?: Record<string, unknown>) => void;
}

/**
 * Adapter for Prometheus time series metrics (counters, gauges, histograms)
 */
interface TimeSeriesMetricsAdapter {
  incrementCounter: (
    name: PromCounterMetricName,
    labels: TimeSeriesMetricLabels,
    value?: number
  ) => void;
  setGauge: (name: PromGaugeMetricName, labels: TimeSeriesMetricLabels, value: number) => void;
  observeTime: (
    name: PromDurationMetricName,
    labels: TimeSeriesMetricLabels
  ) => (start: number, durationMs: number) => void;
  flushBuffer?: () => Promise<void>;
  setUser?: (user: AuthAccountWithPassType | null) => void;
  setViewName?: (name: string) => void;
}

/**
 * Metrics class manages two distinct telemetry systems:
 * - Log aggregation (via MetricsAdapter): Structured logs for events, durations, warnings, errors
 * - Prometheus time series (via TimeSeriesMetricsAdapter): Counter, gauge, and histogram metrics
 */
class Metrics {
  private adapters: MetricsAdapter[] = [];

  private adapterPromise: Promise<MetricsAdapter[]> | null = null;

  private adaptersLoaded = false;

  private timeSeriesAdapters: TimeSeriesMetricsAdapter[] = [];

  private viewName: string | null = null;

  constructor(
    adapterLoader: Promise<MetricsAdapter[]>,
    timeSeriesAdapterLoader?: Promise<TimeSeriesMetricsAdapter[]>
  ) {
    this.adapterPromise = adapterLoader;
    void adapterLoader.then((adapters) => {
      this.adapters = adapters;
      this.adaptersLoaded = true;
    });

    if (timeSeriesAdapterLoader) {
      void timeSeriesAdapterLoader.then((adapters) => {
        this.timeSeriesAdapters = adapters;
      });
    }
  }

  private runWhenReady(action: () => void): void {
    if (!this.adaptersLoaded && this.adapterPromise) {
      void this.adapterPromise.then(action);
    } else {
      action();
    }
  }

  setUser(user: AuthAccountWithPassType | null): void {
    const logUser = () => {
      this.adapters.forEach((adapter) => {
        adapter.setUser(user);
      });
    };

    this.timeSeriesAdapters.forEach((adapter) => {
      if (adapter.setUser) {
        adapter.setUser(user);
      }
    });

    this.runWhenReady(logUser);
  }

  setViewName(name: string): void {
    const computedName = getViewName(name);
    if (this.viewName === computedName) {
      return;
    }
    this.viewName = computedName;
    this.adapters.forEach((adapter) => {
      adapter.setViewName(computedName);
    });

    this.timeSeriesAdapters.forEach((adapter) => {
      if (adapter.setViewName) {
        adapter.setViewName(computedName);
      }
    });
  }

  logDurationMetric(name: DurationMetricName, options?: DurationMetricOptions): OnComplete {
    const start = Date.now();
    const perfStart = performance.now();
    return (modifiedOptions) => {
      const duration = performance.now() - perfStart;
      if (modifiedOptions) {
        options = { ...options, ...modifiedOptions };
      }
      const logDurations = () => {
        for (const adapter of this.adapters) {
          adapter.logDuration(name, options)(start, duration);
        }
      };
      this.runWhenReady(logDurations);
    };
  }

  logTimeSeriesDurationMetric(
    name: PromDurationMetricName,
    labels: TimeSeriesMetricLabels
  ): OnComplete {
    const start = Date.now();
    const perfStart = performance.now();
    return (modifiedOptions) => {
      const duration = performance.now() - perfStart;
      for (const adapter of this.timeSeriesAdapters) {
        adapter.observeTime(name, { ...labels, ...(modifiedOptions ?? {}) })(start, duration);
      }
    };
  }

  incrementTimeSeriesCounter(
    name: PromCounterMetricName,
    labels: TimeSeriesMetricLabels,
    value?: number
  ): void {
    for (const adapter of this.timeSeriesAdapters) {
      adapter.incrementCounter(name, labels, value);
    }
  }

  setTimeSeriesGauge(
    name: PromGaugeMetricName,
    labels: TimeSeriesMetricLabels,
    value: number
  ): void {
    for (const adapter of this.timeSeriesAdapters) {
      adapter.setGauge(name, labels, value);
    }
  }

  logEvent: LogEventFunc = (data) => {
    const logEvent = () => {
      this.adapters.forEach((adapter) => {
        adapter.logEvent(data);
      });
    };
    this.runWhenReady(logEvent);
  };

  logWarning(message: string, options?: Record<string, unknown>): void {
    const logWarning = () => {
      this.adapters.forEach((adapter) => {
        adapter.logWarning(message, options);
      });
    };
    this.runWhenReady(logWarning);
  }

  logError(error: Error, options?: Record<string, unknown>): void {
    const logError = () => {
      this.adapters.forEach((adapter) => {
        adapter.logError(error, options);
      });
    };
    this.runWhenReady(logError);
  }

  /**
   * Flushes any buffered time series metrics immediately.
   * Call this before closing windows/tabs to ensure metrics are sent.
   */
  async flushTimeSeriesMetrics(): Promise<void> {
    if (this.timeSeriesAdapters.length === 0) return;

    // Use Promise.allSettled to ensure all adapters flush even if one fails
    await Promise.allSettled(
      this.timeSeriesAdapters.map(async (adapter) => adapter.flushBuffer?.())
    );
  }
}

export type {
  DurationMetricOptions,
  MetricsAdapter,
  TimeSeriesMetricsAdapter,
  OnDurationComplete,
  OnComplete,
};

export default Metrics;
